const IGNORED_CHARS = /[\s']/g;
const CANONICAL = /^\d+(\.\d+)?$/;
const HAS_DIGIT = /\d/;
const GROUP = /^\d{3}$/;
const LEADING_GROUP = /^\d{1,3}$/;

/**
 * Grouped digits, e.g. `1.234.567` split on `.` — the first group carries 1-3 digits,
 * every following one exactly 3. Rejects `1.5.6`, which would otherwise read as 156.
 */
function isGrouped(value: string, separator: string): boolean {
    const parts = value.split(separator);

    return parts.length > 1
        && LEADING_GROUP.test(parts[0])
        && parts.slice(1).every((part) => GROUP.test(part));
}

function toCanonical(integerPart: string, fractionPart: string | null): string {
    const integer = integerPart === '' ? '0' : integerPart;

    return fractionPart === null || fractionPart === '' ? integer : `${integer}.${fractionPart}`;
}

/**
 * Reads a number the way a user writes it, in either German or English notation, without
 * caring which locale the page runs in — `1,5` and `1.5` both yield 1.5.
 *
 * Grouping separators are accepted and stripped, but only where they actually form groups:
 * `1.234,56` and `1,234.56` both yield 1234.56, while `1.5.6` is rejected. A separator that
 * appears exactly once is always the decimal one, so `1.234` yields 1.234, not 1234.
 *
 * @returns the parsed number, or null for an empty or unreadable input
 */
export function parseLocalizedNumber(raw: string): number | null {
    let value = String(raw ?? '').replace(IGNORED_CHARS, '');

    let sign = '';
    if (value.startsWith('-') || value.startsWith('+')) {
        sign = value.startsWith('-') ? '-' : '';
        value = value.slice(1);
    }

    // A lone separator carries no digits to read, and `,` would otherwise canonicalize to "0".
    if (!HAS_DIGIT.test(value)) {
        return null;
    }

    const dots = value.split('.').length - 1;
    const commas = value.split(',').length - 1;

    let canonical: string;

    if (dots > 0 && commas > 0) {
        const decimalSeparator = value.lastIndexOf('.') > value.lastIndexOf(',') ? '.' : ',';
        const groupSeparator = decimalSeparator === '.' ? ',' : '.';
        const decimalIndex = value.lastIndexOf(decimalSeparator);
        const integerPart = value.slice(0, decimalIndex);

        if (!isGrouped(integerPart, groupSeparator)) {
            return null;
        }

        canonical = toCanonical(integerPart.split(groupSeparator).join(''), value.slice(decimalIndex + 1));
    } else if (dots > 1 || commas > 1) {
        const groupSeparator = dots > 1 ? '.' : ',';

        if (!isGrouped(value, groupSeparator)) {
            return null;
        }

        canonical = toCanonical(value.split(groupSeparator).join(''), null);
    } else if (dots === 1 || commas === 1) {
        const decimalIndex = dots === 1 ? value.indexOf('.') : value.indexOf(',');

        canonical = toCanonical(value.slice(0, decimalIndex), value.slice(decimalIndex + 1));
    } else {
        canonical = value;
    }

    if (!CANONICAL.test(canonical)) {
        return null;
    }

    const parsed = Number(`${sign}${canonical}`);

    return Number.isFinite(parsed) ? parsed : null;
}

export default parseLocalizedNumber;
